import type { JackpotProgressSchema, PublicCampaignSchema } from "@/types";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import type { JackpotCampaign, JackpotView, JackpotWinRow } from "./types";
import { orderedJackpots } from "./types";

/**
 * Join the two streams into the rendered `JackpotCampaign[]`. Pure — the store calls it (via
 * `derive.ts`) after either of them updates, and it must stay clock-free: `Date.now()` in here
 * would defeat the store's `listEqual` short-circuit and re-render every consumer on every push.
 *
 * A campaign is included as soon as its `campaigns` tile arrives, before the progress stream
 * lands — so the go-live tile is renderable, with `events: null` on every jackpot meaning "pot not
 * known yet" rather than "no pot".
 */
export function assembleJackpots(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  jackpotProgressById: Record<CampaignKey, JackpotProgressSchema>
): JackpotCampaign[] {
  const result: JackpotCampaign[] = [];

  for (const campaign of Object.values(campaignsById)) {
    if (campaign.type !== "JA") continue;

    const key = campaignKey(campaign);
    const progress = jackpotProgressById[key];
    const eventsByJackpotId = new Map(
      (progress?.jackpots ?? []).map((entry) => [
        entry.jackpot_id,
        entry.jackpot_events,
      ])
    );

    const jackpots: JackpotView[] = orderedJackpots(campaign).map(
      (jackpot) => ({
        jackpot,
        events: eventsByJackpotId.get(jackpot.id) ?? null,
      })
    );

    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;

    result.push({
      campaign,
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : { value: 0, currency: "" },
      eligible_games: games,
      eligible_games_count: count,
      jackpots,
      recent_wins:
        winsFromProgress(progress, jackpots) ?? winsFromLastEvents(jackpots),
      total: progress?.total ?? null,
    });
  }

  return result;
}

/**
 * The feed rows from the stream's own `recent_wins` tail: the masked name leads, the amount and —
 * when the campaign has more than one jackpot — which one it was sit in the detail cell, amount
 * first so it is the title that gives way if the cell ever runs short. `null` (not `[]`) when the
 * backend predates the tail, so the caller can fall back; an empty array from a backend that has
 * it is authoritative — nobody has won yet.
 */
function winsFromProgress(
  progress: JackpotProgressSchema | undefined,
  jackpots: JackpotView[]
): JackpotWinRow[] | null {
  const wins = progress?.recent_wins;
  if (!Array.isArray(wins)) return null;
  const titleById = new Map(
    jackpots.map((v) => [v.jackpot.id, v.jackpot.title])
  );
  const several = jackpots.length > 1;
  return wins.map((win) => {
    const title = titleById.get(win.jackpot_id);
    return {
      key: `${win.jackpot_id}-${win.won_at}`,
      jackpotId: win.jackpot_id,
      player: typeof win.player === "string" ? win.player : null,
      name: win.name,
      detail:
        several && title
          ? `${win.amount.display} · ${title}`
          : win.amount.display,
      wonAt: win.won_at,
    };
  });
}

/**
 * The feed rows a backend without `recent_wins` can still support: one row per jackpot from its
 * `last` paid event — the amount as the row's name, the jackpot's title as the detail, no player.
 * Newest first. The card's contract (`JackpotWinRow`) is the same either way.
 */
function winsFromLastEvents(jackpots: JackpotView[]): JackpotWinRow[] {
  const rows: JackpotWinRow[] = [];
  for (const view of jackpots) {
    const last = view.events?.last;
    if (!last?.payout_date) continue;
    rows.push({
      key: `${view.jackpot.id}-${last.payout_date}`,
      jackpotId: view.jackpot.id,
      player: null,
      name: last.payout_amount.display,
      detail: view.jackpot.title,
      wonAt: last.payout_date,
    });
  }
  return rows.sort((a, b) => Date.parse(b.wonAt) - Date.parse(a.wonAt));
}
